// Plain-language wording for the feed (CLAUDE.md invariant 8): "waiting for you", never
// "overdue"; phases and work in words a person would use; no shame counters.
import type { DayMoment } from "@shared/agenda";
import type { CardSummary, WorkView } from "@shared/api";
import type { CardHistoryEntry, Urgency } from "@shared/core";
import { clock, freeWords } from "@web/day/dayWords";
import { plainText } from "./card/markdownLite";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "waiting for you · 3 days" from `waitingSinceAt`; null when nothing is waiting. */
export function waitingLabel(since: string | null | undefined, now: number): string | null {
  if (!since) return null;
  const at = new Date(since).getTime();
  if (Number.isNaN(at)) return null;
  const diff = Math.max(0, now - at);
  let span: string;
  if (diff < HOUR) span = "under an hour";
  else if (diff < DAY) span = `${Math.floor(diff / HOUR)} h`;
  else if (diff < 14 * DAY) {
    const days = Math.floor(diff / DAY);
    span = days === 1 ? "1 day" : `${days} days`;
  } else {
    const weeks = Math.floor(diff / (7 * DAY));
    span = `${weeks} weeks`;
  }
  return `waiting for you · ${span}`;
}

export const URGENCY_LABEL: Record<Urgency, string> = {
  now: "Now",
  today: "Today",
  this_week: "This week",
  whenever: "Whenever",
};

const KIND_LABEL: Record<WorkView["kind"], string> = {
  instruction: "Instruction",
  scoped_instruction: "Instruction for a card",
  recollect: "Looking again",
  draft: "Drafting",
  research: "Research",
  rejudge: "Re-sorting this round",
  compound_learning: "Learning from this round",
  execute_approved_action: "Approved action",
  default_cleanup: "Archive",
  routine_action_batch: "Routine cleanup",
};

export function workKindLabel(kind: WorkView["kind"]): string {
  return KIND_LABEL[kind] ?? kind;
}

function firstSentence(text: string | null | undefined): string {
  const t = (text ?? "").trim();
  if (!t) return "";
  return t.length > 160 ? `${t.slice(0, 157)}…` : t;
}

/** What is happening with a work item, in words ("Sent, archiving", "Outcome unknown, …"). */
export function workPhaseLabel(
  work: Pick<WorkView, "lane" | "status" | "phase" | "error" | "scheduledFor">,
): string {
  // Quarantined work is also "blocked"; say why in words the reader can act on.
  if (work.phase === "restore_quarantine") {
    return "Paused after a restore: check Gmail, or choose not to run it";
  }
  if (work.status === "blocked" || work.phase === "blocked") {
    const reason = firstSentence(work.error);
    return reason ? `Blocked: ${reason}` : "Blocked: needs a decision";
  }
  switch (work.status) {
    case "cancelled":
      return "Cancelled";
    case "stale":
      return "Stale: the card changed after you approved";
    case "failed": {
      const reason = firstSentence(work.error);
      return reason ? `Couldn't finish: ${reason}` : "Couldn't finish";
    }
    case "completed":
      return "Done";
    default:
      break;
  }
  if (work.lane === "executor") {
    // U12: an approved send waiting for the time the owner chose.
    if (work.scheduledFor && work.status === "queued") return "Scheduled to send";
    switch (work.phase) {
      case "awaiting_verification":
        return "Waiting to verify";
      case "verified_for_main":
        return "Verified, sending";
      case "main_unknown":
        return "Outcome unknown, checking Gmail";
      case "main_succeeded_cleanup_pending":
        return "Sent, archiving";
      case "completed":
        return "Done";
      default:
        return work.status === "working" ? "Working on it" : "Waiting to verify";
    }
  }
  return work.status === "working" ? "Working on it" : "Waiting for Claude";
}

/** "Needs a decision" styling (never red) for blocked/failed/unknown outcomes. */
export function workNeedsDecision(work: Pick<WorkView, "status" | "phase">): boolean {
  return (
    work.status === "blocked" ||
    work.status === "failed" ||
    work.phase === "blocked" ||
    work.phase === "main_unknown" ||
    work.phase === "restore_quarantine"
  );
}

export function doneLabel(card: Pick<CardSummary, "disposition" | "snoozedUntil">): string {
  if (card.disposition === "dismissed") return "Dismissed";
  if (card.disposition === "snoozed") return "Snoozed";
  return "Completed";
}

export interface ReadableHistory {
  at: string;
  label: string;
  detail: string;
  attention?: boolean;
}

interface HistoryWords {
  label: string | ((actor: CardHistoryEntry["actor"]) => string);
  /** Shown when the entry has no detail, or always when `detail` is machine text. */
  detail?: string;
  /** Turn the server's detail into words; return null to use `detail`. */
  mapDetail?: (detail: string) => string | null;
  attention?: boolean;
}

const ignoreDetail = () => null;
const kindWords = (d: string) =>
  d in KIND_LABEL ? KIND_LABEL[d as WorkView["kind"]].toLowerCase() : null;

/** The server's card history vocabulary (server/core, server/gateway), in plain words. */
const HISTORY_WORDS: Record<string, HistoryWords> = {
  "agent.created": { label: "Claude prepared", detail: "This card." },
  "agent.updated": { label: "Claude updated", detail: "This card." },
  "agent.refreshed": { label: "Claude refreshed", detail: "This card after new mail." },
  "user.edited": { label: "You edited", detail: "The draft.", mapDetail: ignoreDetail },
  "user.dismissed": {
    label: "You dismissed",
    detail: "Removed from review. Gmail was not changed.",
  },
  "user.snoozed": { label: "You snoozed" },
  "card.restored": { label: "Back for review", detail: "You returned this card to review." },
  "card.unsnoozed": { label: "Back from snooze", detail: "The snooze ended." },
  "work.queued": {
    label: (actor) => (actor === "user" ? "You asked" : "Queued"),
    mapDetail: (d) => {
      const words = kindWords(d);
      return words ? `For ${words}.` : d;
    },
  },
  "work.claimed": {
    label: "Started",
    mapDetail: (d) => {
      const words = kindWords(d);
      return words ? `${words[0]!.toUpperCase()}${words.slice(1)}.` : d;
    },
  },
  "work.completed": { label: "Claude did", detail: "Finished the requested work." },
  "work.failed": { label: "Couldn't finish", attention: true },
  "work.blocked": { label: "Blocked", attention: true },
  "work.stale": { label: "Needs review", attention: true },
  "work.cancelled": {
    label: (actor) => (actor === "user" ? "You cancelled" : "Cancelled"),
    detail: "The queued work.",
  },
  "work.retried": { label: "You retried", detail: "Queued again." },
  "work.recovered": { label: "You recovered", detail: "It's back in the queue." },
  "work.released": { label: "Paused", detail: "Back in the queue for later." },
  "approval.stale": {
    label: "Needs review",
    detail: "The approval expired because the card changed. Check the current next step.",
    attention: true,
  },
  "send_later.returned": {
    label: "Back for you",
    detail: "Someone wrote since you scheduled this, so it didn't go out.",
    attention: true,
  },
  "action.main_succeeded": { label: "Sent", detail: "Gmail confirmed it." },
  "action.completed": { label: "Done", detail: "Gmail confirmed every check." },
  "sweep.ranked": {
    label: "Re-sorted",
    detail: "After your note on this sweep.",
    mapDetail: ignoreDetail,
  },
  "sweep.removed": {
    label: "Set aside",
    detail: "After your note on this sweep.",
    mapDetail: ignoreDetail,
  },
  "task.attached": {
    label: "Task created",
    detail: "Added to your tasks.",
    mapDetail: ignoreDetail,
  },
  "research.attached": {
    label: "Research added",
    detail: "Findings are on this card.",
    mapDetail: ignoreDetail,
  },
  // Wording for types other runtimes (and the mock API) use.
  "user.instruction": { label: "You asked" },
  "user.scoped_instruction": { label: "You asked" },
  "user.approved_action": { label: "You approved", detail: "The next step shown then." },
  "user.default_cleanup_approved": { label: "You approved", detail: "Archive this thread." },
  "user.edited_artifact": { label: "You edited", detail: "The draft." },
  "user.restored": { label: "Back for review", detail: "You returned this card to review." },
  "agent.completed": { label: "Claude did" },
  "runtime.source_changed": {
    label: "Needs review",
    detail: "New reply arrived after you approved.",
    attention: true,
  },
  "runtime.sent": { label: "Sent", detail: "Gmail confirmed the message." },
  "runtime.archived": { label: "Archived", detail: "Gmail confirmed the archive." },
};

function humanize(type: string): string {
  const tail = type.includes(".") ? type.slice(type.indexOf(".") + 1) : type;
  const words = tail.replace(/[_.]/g, " ").trim();
  return words ? words[0]!.toUpperCase() + words.slice(1) : type;
}

/** "You asked / Claude did" lines for a card's history. Unknown types still read naturally. */
export function readableHistory(entries: readonly CardHistoryEntry[]): ReadableHistory[] {
  return entries.map((entry) => {
    const known = HISTORY_WORDS[entry.type];
    if (known) {
      const mapped =
        entry.detail !== undefined && known.mapDetail ? known.mapDetail(entry.detail) : undefined;
      const detail =
        mapped === undefined ? (entry.detail ?? known.detail) : (mapped ?? known.detail);
      return {
        at: entry.at,
        label: typeof known.label === "function" ? known.label(entry.actor) : known.label,
        detail: detail ?? "",
        ...(known.attention ? { attention: true } : {}),
      };
    }
    const label =
      entry.actor === "user" ? "You" : entry.actor === "agent" ? "Claude did" : "MeeMail";
    return { at: entry.at, label, detail: entry.detail ?? humanize(entry.type) };
  });
}

/**
 * A row's second line. A "Needs you" row says what is asked (else the next thing, else why),
 * never the rule that let it through. A waiting row says what is asked, else why. The row's
 * "→ next thing" chip stays beside it (Claude's suggested step), unless it is the same words.
 */
export function rowSecondLine(
  card: Pick<CardSummary, "ask" | "nextThing" | "why">,
  variant: "row" | "needsYou" = "row",
): { text: string; isAsk: boolean } {
  const ask = card.ask?.text?.trim();
  if (ask) return { text: ask, isAsk: true };
  const next = card.nextThing?.trim();
  if (variant === "needsYou" && next) return { text: next, isAsk: true };
  return { text: plainText(card.why), isAsk: false };
}

function weekday(now: number, timeZone: string | undefined): string {
  try {
    return new Intl.DateTimeFormat("en-US", { weekday: "long", timeZone }).format(now);
  } catch {
    return new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(now);
  }
}

/**
 * The round header's day line: "Sunday, free for the rest of today", "Sunday, next: Staff
 * meeting at 3:00 PM", "Sunday, now: Standup until 10:30 AM". Without a calendar (moment null),
 * the weekday alone.
 */
export function roundDayLine(
  now: number,
  timeZone: string | undefined,
  moment: DayMoment | null,
): string {
  const day = weekday(now, timeZone);
  if (!moment) return day;
  const zone = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const current = moment.now[0];
  if (current) {
    return current.end
      ? `${day}, now: ${current.title} until ${clock(current.end, zone)}`
      : `${day}, now: ${current.title}`;
  }
  if (moment.next?.start)
    return `${day}, next: ${moment.next.title} at ${clock(moment.next.start, zone)}`;
  const free = freeWords(moment.free, zone);
  return free ? `${day}, ${free.charAt(0).toLowerCase()}${free.slice(1)}` : day;
}

/** "Needs you · 4" and, when more need you than are shown, "1 more needs you, below". */
/** "Waiting · 9": the entries below "Needs you" (a bundle of alike cards is one entry). */
export function waitingWords(entries: number): string {
  return `Waiting · ${entries}`;
}

export function needsYouWords(
  total: number,
  moreBelow: number,
): { label: string; note: string | null } {
  return {
    label: `Needs you · ${total}`,
    note: moreBelow > 0 ? `${moreBelow} more need${moreBelow === 1 ? "s" : ""} you, below` : null,
  };
}
